// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {MouseEvent} from 'react';

import styled from 'styled-components';

import {Post} from 'mattermost-redux/types/posts';
import {FileInfo} from 'mattermost-redux/types/files';
import {DispatchFunc, GetStateFunc} from 'mattermost-redux/types/actions';
import {ExtendedPost} from 'mattermost-redux/actions/posts';
import {localizeMessage} from 'utils/utils';

type CreatePostAction = (post: Post, files?: FileInfo[]) => (dispatch: DispatchFunc) => Promise<{data: boolean}>;
type RemovePostAction = (post: ExtendedPost) => (dispatch: DispatchFunc, getState: GetStateFunc) => void;

type Props = {
    post: Post;
    actions: {
        createPost: CreatePostAction;
        removePost: RemovePostAction;
    };
    isRhs?: boolean;
};

const RetryAction = styled.button`
    background: none;
    border: none;
    opacity: 0.8;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    border-radius: 4px;

    &:hover {
        opacity: 1;
        background: rgba(var(--center-channel-color-rgb), 0.08);
    }

    & i {
        color: rgba(210, 75, 78, 1);
    }
`;

const RemoveAction = styled.button`
    background: none;
    border: none;
    opacity: 0.8;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
        opacity: 1;
        background: rgba(var(--center-channel-color-rgb), 0.08);
    }

    & i {
        color: var(--center-channel-color-88);
    }
`;

const Actions = styled.div<{isRhs?: boolean}>`
    position: absolute;
    display: flex;
    right: ${(props) => (props.isRhs ? '45px' : '12px')};
    top: 0;
    z-index: 100;
`;

export default class FailedPostOptions extends React.PureComponent<Props> {
    retryPost = (e: MouseEvent): void => {
        e.preventDefault();

        const post = {...this.props.post};
        Reflect.deleteProperty(post, 'id');
        this.props.actions.createPost(post);
    };

    cancelPost = (e: MouseEvent): void => {
        e.preventDefault();

        this.props.actions.removePost(this.props.post);
    };

    render(): JSX.Element {
        return (
            <Actions isRhs={this.props.isRhs}>
                <RetryAction
                    aria-label={localizeMessage('pending_post_actions.retry', 'Retry')}
                    data-test-id='post-retry'
                    onClick={this.retryPost}
                >
                    <i className='fa fa-rotate-right' />
                </RetryAction>
                <RemoveAction
                    aria-label={localizeMessage('pending_post_actions.cancel', 'Cancel')}
                    data-test-id='post-cancel'
                    onClick={this.cancelPost}
                >
                    <i className='fa fa-close' />
                </RemoveAction>
            </Actions>
        );
    }
}
